import { useCallback, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useFetchedSelectionsRequestsHook } from "../useFetchedSelectionsRequestsHook";
import { useUsersHook } from "../useUsersHook";
import { userActionSet } from "../../redux/actions/userActions";
import { nsKey } from "../../utils/storage/partnerStorage";

// ─── Storage Keys ────────────────────────────────────────────────────────────
// Layout-storage namespace shared by the PipelineTableScreen boards that opt in.
// They pass it as `layoutScope`, so a layout saved on one shows up in the
// others' menus (they render the same column set). Each board still keeps its
// own default-layout pointer, keyed by its storageKeyPrefix — see the
// *-default-layout entries below.
//
// WHICH boards opt in is per-repo (eastviewos: Pre-Close + Post Close;
// lendmarq-los: Pre-Close + Closing — those are the two boards in its header
// nav). All three pointers are registered here either way so this file stays
// identical across the sibling repos; an unregistered board simply never writes.
export const BOARD_LAYOUT_SCOPE = "board-pipeline";

// All localStorage keys managed by this hook, mapped to their user profile path
const SYNCED_KEYS = {
  "lo-pipeline-layouts": {
    profileKey: "pipelineLayouts",
    parse: true,
    scope: "pipeline",
  },
  "lo-pipeline-default-layout": {
    profileKey: "pipelineDefaultLayout",
    parse: false,
    scope: "pipeline",
  },
  "lo-pipeline-assigned-to-me": {
    profileKey: "pipelineAssignedToMe",
    parse: false,
    scope: "pipeline",
  },
  // "Remember where I left off" toggle. The snapshot of the last view itself
  // (lo-pipeline-last-session) stays device-local — only the on/off preference
  // syncs to the profile.
  "lo-pipeline-remember-session": {
    profileKey: "pipelineRememberSession",
    parse: false,
    scope: "pipeline",
  },
  "lo-pipeline-favorite-loans": {
    profileKey: "pipelineFavoriteLoans",
    parse: true,
    scope: "pipeline",
  },
  "lo-pipeline-show-favorites": {
    profileKey: "pipelineShowFavorites",
    parse: false,
    scope: "pipeline",
  },
  // ── Pre-Close / Post-Close boards (PipelineTableScreen) ──
  // One shared layouts list across both boards…
  [`${BOARD_LAYOUT_SCOPE}-layouts`]: {
    profileKey: "boardLayouts",
    parse: true,
    scope: "pipeline",
  },
  // …but a per-board "which layout opens by default" pointer, since the boards
  // show different slices of the pipeline.
  "pre-close-pipeline-default-layout": {
    profileKey: "preCloseDefaultLayout",
    parse: false,
    scope: "pipeline",
  },
  "closing-pipeline-default-layout": {
    profileKey: "closingDefaultLayout",
    parse: false,
    scope: "pipeline",
  },
  "post-close-pipeline-default-layout": {
    profileKey: "postCloseDefaultLayout",
    parse: false,
    scope: "pipeline",
  },
  // ── Full Pipeline board ──
  // Keeps its OWN layouts list rather than joining BOARD_LAYOUT_SCOPE: it adds
  // the Pipeline column and slices by pipelineStage, so its views don't fit the
  // Pre-Close / Post-Close boards (or theirs it).
  "full-pipeline-layouts": {
    profileKey: "fullPipelineLayouts",
    parse: true,
    scope: "pipeline",
  },
  "full-pipeline-default-layout": {
    profileKey: "fullPipelineDefaultLayout",
    parse: false,
    scope: "pipeline",
  },
};
const PROFILE_PREFS_KEY = "preferences";

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * The signed-in user's Mongo id.
 *
 * Redux `user` is mirrored from GET /auth/me (see auth/AuthProvider), whose
 * projection calls it `user_id` — it is `str(user["_id"])`, the same value the
 * update endpoint keys on. Older code paths (GET /users/logged_in_user, rows out
 * of the all_users list) carry `_id` instead, so accept either. Reading only
 * `_id` is why preference syncing silently no-opped after the auth swap.
 */
const resolveUserId = (u) => u?._id || u?.user_id || u?.id || null;

const emailOf = (u) => u?.emailAddress || u?.email || null;

/**
 * The caller's own row out of the app-wide `users` slice (raw Mongo docs from
 * GET /users/all_users). /auth/me does NOT project `preferences`, so this is
 * where the saved profile preferences are actually read from.
 */
const findOwnUserRow = (list, email) => {
  if (!Array.isArray(list) || !email) return null;
  const lc = String(email).toLowerCase();
  return list.find((u) => String(u?.emailAddress || "").toLowerCase() === lc);
};

// Module-scoped so several components mounting this hook at once (a board, the
// drilldown favorite button) don't each fire the same all-users fetch.
let allUsersRequested = false;

/**
 * Returns true if a value from the profile or localStorage is a garbage/sentinel
 * value that should be treated as "not set". Guards against Python's None being
 * serialized as the literal string "None", as well as other bad values.
 */
const isGarbageValue = (val) =>
  val === null ||
  val === undefined ||
  val === "None" ||
  val === "null" ||
  val === "undefined" ||
  val === "";

/**
 * Validates that a profile value has the correct type for its storage key.
 * - parse: true keys (arrays) must be actual arrays
 * - parse: false keys (strings) must be non-garbage strings
 * Returns the sanitized value or null if invalid.
 */
const sanitizeProfileValue = (value, parse) => {
  if (isGarbageValue(value)) return null;

  if (parse) {
    // parse:true keys expect arrays (pipelineLayouts, pipelineFavoriteLoans)
    return Array.isArray(value) ? value : null;
  }

  // parse:false keys expect simple strings ("true", "false", layout IDs)
  return typeof value === "string" ? value : null;
};

/**
 * Read all synced localStorage keys into a flat object keyed by profileKey.
 * All values are validated for correct type before being returned.
 */
const readAllLocalStoragePrefs = () => {
  const pipeline = {};
  const root = {};

  for (const [storageKey, { profileKey, parse, scope }] of Object.entries(
    SYNCED_KEYS,
  )) {
    try {
      const raw = localStorage.getItem(nsKey(storageKey));
      if (isGarbageValue(raw)) {
        (scope === "root" ? root : pipeline)[profileKey] = null;
        continue;
      }
      if (parse) {
        const parsed = JSON.parse(raw);
        (scope === "root" ? root : pipeline)[profileKey] = Array.isArray(parsed)
          ? parsed
          : null;
      } else {
        (scope === "root" ? root : pipeline)[profileKey] = raw;
      }
    } catch {
      (scope === "root" ? root : pipeline)[profileKey] = null;
    }
  }
  return { pipeline, root };
};

/**
 * Write profile preferences back into localStorage (used on hydration).
 * Values are validated before writing to prevent corrupted data.
 */

// ─── Hook ────────────────────────────────────────────────────────────────────

/**
 * useUserPreferences
 *
 * Keeps localStorage pipeline preferences in sync with the user profile.
 *
 * Hydration (profile → localStorage):
 *   On mount, if the user profile has saved preferences but localStorage is
 *   missing data, it writes the profile values into localStorage so a user
 *   logging in on a new device gets their settings immediately.
 *
 * Sync (localStorage → profile):
 *   Call `syncPreferences()` after any localStorage write. It debounces and
 *   batches all managed keys into a single profile update.
 *
 * Usage in LoanOfficerPipelineScreen:
 *   const { syncPreferences } = useUserPreferences();
 *   // After any localStorage write:
 *   persistLayouts(updated);
 *   syncPreferences();
 */
export const useUserPreferences = () => {
  const dispatch = useDispatch();
  const user = useSelector((state) => state?.user);
  // Raw user docs (GET /users/all_users) — the only client-reachable payload
  // that carries `preferences`, since /auth/me omits it and /users/user/{id}
  // rebuilds the doc through B2BUser.to_dict(), which drops unknown fields.
  const allUsers = useSelector((state) => state?.users);
  const { updateUserById } = useUsersHook();
  const { getAllUsers } = useFetchedSelectionsRequestsHook();

  const userRef = useRef(user);
  const syncTimerRef = useRef(null);
  const hydratedRef = useRef(false);

  // Keep userRef current so the debounced sync always uses latest user
  useEffect(() => {
    userRef.current = user;
  }, [user]);

  // ── Hydration: profile → localStorage (runs once when user loads) ──
  useEffect(() => {
    if (hydratedRef.current || !resolveUserId(user)) return;

    // Prefer prefs already on the Redux user; otherwise pull them off the
    // caller's own row in the all-users list, fetching that list once if it
    // hasn't loaded yet (this effect re-runs when the slice lands).
    let profilePrefs = user?.[PROFILE_PREFS_KEY];
    if (!profilePrefs) {
      const ownRow = findOwnUserRow(allUsers, emailOf(user));
      if (!ownRow) {
        if (!allUsersRequested && !Array.isArray(allUsers)) {
          allUsersRequested = true;
          getAllUsers({});
        }
        return;
      }
      profilePrefs = ownRow?.[PROFILE_PREFS_KEY];
      // Park them on the Redux user so the debounced sync merges onto the
      // stored prefs instead of overwriting them with a partial object.
      if (profilePrefs) {
        dispatch(
          userActionSet({ ...userRef.current, [PROFILE_PREFS_KEY]: profilePrefs }),
        );
      }
    }

    hydratedRef.current = true;
    if (!profilePrefs) return;

    let didHydrate = false;
    for (const [storageKey, { profileKey, parse, scope }] of Object.entries(
      SYNCED_KEYS,
    )) {
      const localValue = localStorage.getItem(nsKey(storageKey));
      const source = scope === "root" ? profilePrefs : profilePrefs?.pipeline;
      const profileValue = source?.[profileKey];
      const sanitized = sanitizeProfileValue(profileValue, parse);

      if (localValue === null && sanitized !== null) {
        try {
          localStorage.setItem(
            nsKey(storageKey),
            parse ? JSON.stringify(sanitized) : String(sanitized),
          );
          didHydrate = true;
        } catch {
          // skip
        }
      }
    }

    if (didHydrate) {
      window.dispatchEvent(new Event("preferences-hydrated"));
    }
  }, [user, allUsers, dispatch, getAllUsers]);

  // ── Sync: localStorage → profile (debounced) ──
  const runSync = useCallback(
    () => {
      const currentUser = userRef.current;
      const userId = resolveUserId(currentUser);
      if (!userId) return;

      const { pipeline: pipelinePrefs, root: rootPrefs } =
        readAllLocalStoragePrefs();
      const existingPrefs = currentUser?.[PROFILE_PREFS_KEY] || {};
      const nextPrefs = {
        ...existingPrefs,
        // Root-level keys as siblings to pipeline
        ...rootPrefs,
        pipeline: {
          ...existingPrefs?.pipeline,
          ...pipelinePrefs,
          updatedAt: new Date().toISOString(),
        },
      };

      updateUserById({
        userId,
        data: { [PROFILE_PREFS_KEY]: nextPrefs },
        onSuccessFn: (updatedUser) => {
          // Merge the saved prefs onto the /auth/me-shaped Redux user rather
          // than replacing it with the raw Mongo doc — the two projections have
          // different fields, and consumers read the /auth/me one.
          dispatch(
            userActionSet({
              ...userRef.current,
              [PROFILE_PREFS_KEY]:
                updatedUser?.[PROFILE_PREFS_KEY] || nextPrefs,
            }),
          );
        },
        onErrorFn: (err) => {
          console.error("Failed to sync preferences to profile:", err);
        },
      });
    },
    [updateUserById, dispatch],
  );

  // Keep the latest runSync reachable from the unmount flush below, which can't
  // depend on it without re-registering (and thus flushing) on every render.
  const runSyncRef = useRef(runSync);
  useEffect(() => {
    runSyncRef.current = runSync;
  }, [runSync]);

  const syncPreferences = useCallback(() => {
    if (syncTimerRef.current) {
      clearTimeout(syncTimerRef.current);
    }
    syncTimerRef.current = setTimeout(() => {
      syncTimerRef.current = null;
      runSyncRef.current?.();
    }, 1500);
  }, []);

  // On unmount, FLUSH a pending sync instead of dropping it — navigating away
  // within the debounce window (save a layout, click straight into a loan) used
  // to silently lose the write.
  useEffect(() => {
    return () => {
      if (syncTimerRef.current) {
        clearTimeout(syncTimerRef.current);
        syncTimerRef.current = null;
        runSyncRef.current?.();
      }
    };
  }, []);

  return { syncPreferences };
};
